import type { CapturedShot } from './use-browser-session';

/**
 * Whether what we just screenshotted is worth putting on a board.
 *
 * Only an instant run asks. When somebody drives the browser themselves they
 * can see what is on screen and press the button anyway — that is their call,
 * and second-guessing it would be the app arguing with its user. Nobody is
 * looking during an instant run, so this is the only thing standing between a
 * bot wall and a board of twenty findings about a "Just a moment…" page.
 *
 * Both checks need the page to be nearly empty as well as to say the wrong
 * thing. A real product may legitimately have an unlucky title, or mention
 * `ERR_CONNECTION_REFUSED` in its own docs, and throwing that away would be
 * worse than the thing being guarded against.
 */

/**
 * Chromium's own network-error page, which is a *committed navigation*.
 *
 * This is the part that is not obvious, and it is measured rather than
 * guessed. Pointing the real worker at a domain that does not resolve gives:
 *
 *     url -> chrome-error://chromewebdata/
 *     dom.title = ""   copyItems = 0   usabilityItems = 0
 *
 * So `pageStatus` goes `ready` exactly as it does for a real page, and there
 * is no text to match on — the scheme and the emptiness are the whole signal.
 * A heuristic that only read the title would let every dead domain through,
 * which is what this one used to do.
 */
const ERROR_SCHEME = 'chrome-error://';

/** For an error page that does carry words — a server's own 502, say, rather
 *  than Chromium's. */
const ERROR_PAGE_TEXT =
  /ERR_[A-Z_]{4,}|site can.t be reached|took too long to respond|refused to connect|server IP address could not be found/i;

/** Bot walls return HTTP 200 and screenshot perfectly, so nothing in the
 *  network layer can see them. The title is what gives them away. */
const CHALLENGE_TITLE =
  /just a moment|attention required|access denied|verify you are human|checking your browser|are you a robot/i;

/** A wall and an error page are both nearly empty. This is the second half of
 *  every check below. */
const MAX_COPY_ITEMS = 8;

/**
 * `empty` is a suspicion, the others are conclusions.
 *
 * A page with nothing extractable on it is *usually* an error page, but it is
 * also what a single-page app looks like for the moment between its document
 * arriving and its first render. Telling the two apart needs another look a
 * second later, which is the caller's job — so this reports what it saw rather
 * than deciding the run's fate.
 */
export type PageVerdict = 'blocked' | 'empty' | 'error' | 'ok';

interface ShotDom {
  copyItems?: { text?: string }[];
  title?: string;
  url?: string;
  usabilityItems?: unknown[];
}

export function judgeCapturedPage(shot: CapturedShot): PageVerdict {
  const dom = shot.dom as ShotDom | null;

  // Exact, and checked before anything else: this is what a dead domain
  // actually looks like. Both places the URL reaches us are checked, since the
  // snapshot is optional and the page URL is not.
  if (
    (dom?.url ?? '').startsWith(ERROR_SCHEME) ||
    shot.pageUrl.startsWith(ERROR_SCHEME)
  ) {
    return 'error';
  }

  // No DOM snapshot means no evidence either way, and refusing a capture on no
  // evidence is how a working page gets thrown away.
  if (!dom) return 'ok';

  const title = dom.title ?? '';
  const items = dom.copyItems ?? [];

  // Nothing extractable at all. Measured on a dead domain: 0 copy items and 0
  // usability items, where a real page had 136 and 195. But an app that
  // renders its own body is briefly this too, so it is reported as a
  // suspicion — see `PageVerdict`.
  if (items.length === 0 && (dom.usabilityItems ?? []).length === 0) {
    return 'empty';
  }

  if (items.length > MAX_COPY_ITEMS) return 'ok';

  const text = items.map((item) => item.text ?? '').join(' ');
  if (ERROR_PAGE_TEXT.test(title) || ERROR_PAGE_TEXT.test(text)) return 'error';
  if (CHALLENGE_TITLE.test(title)) return 'blocked';
  return 'ok';
}

/** What to tell somebody who watched their page fail to open. */
export const PAGE_VERDICT_MESSAGE: Record<
  Exclude<PageVerdict, 'ok'>,
  string
> = {
  blocked:
    'That site blocked our browser. Capture it with the Chrome extension instead.',
  empty: "That page didn't render anything we could read.",
  error: "That page didn't load. Check the address and try again.",
};
